// Counter Intelligence — in-chat deduplication / delete offer helpers.
//
// After the 'duplicates' how-to answer explains the rules (a true 100% duplicate
// can't exist; look-alikes are unit/pack/number/spelling variations; deleting is a
// soft archive), this offers to actually RUN the look-alike scan right in the chat —
// mirroring the item-import and price-update offers. Without this, the explanation
// dead-ends: the user is told to "ask me to find duplicates" but the follow-up has
// to survive a cold re-classification. The offer + pending makes it deterministic.
//
// Pure (no React / no I/O) so it is unit-testable.

const DEDUP_TTL_MS = 30 * 60 * 1000; // 30 minutes, same as the other CI pendings

// A decline wins even if it also contains an affirm-ish word. Affirmatives are the
// clear yes-words plus the dedup verbs a user would naturally reach for. Kept tight
// (no bare "show"/"look") so an unrelated command typed at the offer doesn't misfire.
const DECLINE_RE = /\b(no|not now|nope|nah|later|never ?mind|nevermind|cancel|stop|forget it|no thanks|i'?m good|all set|skip)\b/i;
const AFFIRM_RE = /\b(yes|yep|yeah|yup|sure|okay|ok|please|do it|go ahead|sounds good|find|search|scan|clean|tidy|de-?dupe|dedupe|duplicates?|look-?alikes?|copies|whole catalog|entire catalog)\b/i;

const nowIso = () => new Date().toISOString();
const plusMs = (ms) => new Date(Date.now() + ms).toISOString();

// Deterministic detector for "please run the duplicate review" requests. The drawer
// checks this BEFORE the model classifier so a dedup request reliably STARTS the scan
// instead of depending on the model (or a freshly-deployed edge function) routing it
// to the deduplicate action. Returns false for genuine how-to questions ("why do I
// have duplicates?") so those still get the explanation + the "Find duplicates now"
// offer rather than an immediate scan.
const DEDUP_HOWTO_RE = /\b(how (do|can|does|would|to)|why (do|does|is|are|can|would)|what (is|are|does|counts|happens|do)|what'?s|explain|difference between|tell me about|is it possible)\b/i;
const DEDUP_TOPIC_RE = /\b(duplicat\w*|dupes?|dups?|look-?alikes?|de-?dup\w*|dedup\w*|copies of the same|same items?)\b/i;
const DEDUP_ACTION_RE = /\b(find|finding|check|checking|scan|scanning|clean|cleaning|tidy|remove|removing|delete|deleting|archive|archiving|look|looking|show|review|reviewing|see|get rid|merge|consolidate|fix|search)\b/i;
const DEDUP_CLUTTER_RE = /\b(clutter|cluttered|messy|too many (of the same|copies|versions)|lots of (the same|copies))\b/i;
const DEDUP_REQUESTY_RE = /\b(can you|could you|would you|will you|please|do it|for me|go ahead|in my (catalog|inventory|catalogue))\b/i;
const DEDUP_DIRECTQ_RE = /\b(do i have|have i got|are there|got any|any)\b/i;

export function matchDedupIntent(text) {
  const t = String(text || '');
  if (!t.trim()) return false;
  if (DEDUP_HOWTO_RE.test(t)) return false;   // a how-to question → explain, don't run
  if (DEDUP_CLUTTER_RE.test(t)) return true;
  if (!DEDUP_TOPIC_RE.test(t)) return false;  // must actually be about duplicates
  return DEDUP_ACTION_RE.test(t) || DEDUP_REQUESTY_RE.test(t) || DEDUP_DIRECTQ_RE.test(t);
}

export function isDedupDecline(text) {
  return DECLINE_RE.test(String(text || ''));
}

export function isDedupAffirmative(text) {
  const t = String(text || '');
  if (DECLINE_RE.test(t)) return false;
  return AFFIRM_RE.test(t);
}

export function buildDedupOfferPending({ text } = {}) {
  return {
    kind: 'pending_dedup',
    awaiting: 'offer',
    original_user_message: String(text || ''),
    created_at: nowIso(),
    expires_at: plusMs(DEDUP_TTL_MS),
  };
}

export function dedupOfferChips() {
  return [
    { key: 'dedup_yes', label: 'Find duplicates now' },
    { key: 'dedup_no', label: 'Not now' },
  ];
}

// The short, actionable nudge shown under the 'duplicates' explanation.
export const DEDUP_OFFER_TEXT =
  'Want me to look now? I’ll scan your catalog for look-alike items, group the matches so you can see how they differ, and let you pick which to archive — nothing is removed until you confirm.';
